import { useMemo } from 'react';

/**
 * Permission helper for coach role-based access control.
 *
 * Backend contract (from GET /api/user/permissions):
 *   {
 *     is_head_coach: bool,
 *     permissions: { [area]: bool },
 *     access: { [area]: 'none' | 'view' | 'edit' }
 *   }
 *
 * Areas: players, schedule, stats, videos, communication, reports, practice_plans
 *
 * Head coaches always have edit access.  If permissions haven't loaded yet
 * (null), we default to edit access to avoid a flash of read-only UI.
 */
export default function usePermissions(permissions) {
  return useMemo(() => {
    const isHead = !permissions || permissions.is_head_coach === true;
    const access = permissions?.access || {};

    const accessLevel = (area) => {
      if (isHead) return 'edit';
      return access[area] || 'edit';
    };

    const canEdit = (area) => accessLevel(area) === 'edit';
    const canView = (area) => accessLevel(area) !== 'none';
    const isReadOnly = (area) => accessLevel(area) === 'view';

    return { isHeadCoach: isHead, accessLevel, canEdit, canView, isReadOnly };
  }, [permissions]);
}
